/** Continuous, pointer-anchored view transform for the reader. See
 *  docs/architecture/frontend/zoom-and-raster-pipeline.md.
 *
 *  The one idea: split the LIVE view transform (cheap, every frame, GPU
 *  compositor) from CONTENT rasterization (expensive pdf.js re-render, deferred
 *  until motion settles). The controller owns a single matrix
 *  `translate(tx,ty) scale(scale/rasterScale)` applied to a "world" element; the
 *  pages inside it are rasterized at `rasterScale`. During a gesture only
 *  `scale`/`tx`/`ty` move — the existing canvas is GPU-scaled (briefly soft) —
 *  and `rasterScale` is bumped to `scale` once idle, which re-rasters sharp.
 *
 *  Coordinates:
 *   - "screen" / pointer coords are relative to the viewport (container) origin.
 *   - `tx,ty` are screen-pixel translation of the world origin.
 *   - the rendered factor `f = scale / rasterScale` is the only place rasterScale
 *     enters; all anchoring math is in screen pixels and is rasterScale-free. */

/** How hard the paper pulls back while a drag is out of bounds.
 *
 * A FRACTION, not a ceiling. The first version capped overshoot at 110px with
 * an asymptotic curve, so past about 150px of finger travel the page simply
 * stopped moving and the gesture went dead in the hand — the same wall the
 * elastic was added to remove, just further out. There is no cap now: the paper
 * follows you as far as you drag, at 55% of your travel, so it always feels
 * attached and always feels like it is resisting.
 */
const RUBBER = 0.55;

/** The spring's per-frame pull, as a fraction of the remaining distance.
 *
 * It RISES with how far out of bounds you are, which is what makes a long
 * overshoot snap back rather than crawl: at a fixed fraction, exponential decay
 * takes the same time from any distance, so a 600px pull returned at exactly
 * the pace of a 30px one and read as sluggish. Floors at a gentle settle for
 * the last few pixels and caps below 1 so it can never overshoot the clamp. */
const SPRING_MIN = 0.2;
const SPRING_MAX = 0.55;
/** Distance, in px, over which the pull climbs from MIN to MAX. */
const SPRING_RAMP = 520;

export const clamp = (v: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, v));

/** New translate on one axis so the screen point `p` stays fixed when the scale
 *  changes `s -> sNext`. Derivation: a world point under `p` satisfies
 *  `p = t + f·q`; holding `q` fixed across the scale change gives
 *  `t' = p − (sNext/s)·(p − t)` (the `f`s cancel, so this is rasterScale-free). */
export const anchorTranslate = (
  t: number,
  p: number,
  s: number,
  sNext: number,
): number => p - (sNext / s) * (p - t);

export interface ViewportInit {
  min?: number;
  max?: number;
  /** px of slack allowed past the content edges before clamping bites. */
  margin?: number;
  /** Screen px kept clear at the top — see `topReserve`. */
  topReserve?: number;
  /** ms of stillness before a re-raster is requested. */
  commitDelay?: number;
  /** Called after every transform change (throttle in the consumer). */
  onChange?: () => void;
  /** Re-raster request: the viewer should render pages at `scale`, then call
   *  `commitRaster()`. Skipped while `canCommit` returns false. */
  onCommit?: (scale: number) => void;
  /** Gate that defers a commit (e.g. while a pen stroke is mid-flight, so we
   *  never re-mount the page under an active stroke). */
  canCommit?: () => boolean;
}

/** Owns the live view transform. Framework-agnostic and side-effect-free except
 *  for the one element it writes `transform` to — so the math is unit-testable
 *  and a different substrate (tiling, WebGL) can reuse it by swapping `onCommit`. */
export class ViewportController {
  tx = 0;
  ty = 0;
  scale = 1;
  /** Scale the page DOM was last rasterized at; `transform` carries the delta. */
  rasterScale = 1;

  min: number;
  max: number;
  margin: number;
  commitDelay: number;

  private world: HTMLElement | null = null;
  private onChange?: () => void;
  private onCommit?: (scale: number) => void;
  private canCommit: () => boolean;
  private commitTimer: ReturnType<typeof setTimeout> | null = null;
  /** Intrinsic content size in rasterScale-space (world layout box). */
  private contentW = 0;
  private contentH = 0;
  /** Content ORIGIN in intrinsic/scene space. Zero for the paged world (content
   *  starts at the world top-left); an infinite canvas's ink can start at NEGATIVE
   *  scene coords, so the clamp + scrollbars must know where the box begins. */
  private ox = 0;
  private oy = 0;
  /** Viewport (container) size in screen px. */
  private viewW = 0;
  private viewH = 0;

  constructor(init: ViewportInit = {}) {
    this.min = init.min ?? 0.3;
    this.max = init.max ?? 8;
    this.margin = init.margin ?? 64;
    this.topReserve = init.topReserve ?? 0;
    this.commitDelay = init.commitDelay ?? 160;
    this.onChange = init.onChange;
    this.onCommit = init.onCommit;
    this.canCommit = init.canCommit ?? (() => true);
  }

  /** The rendered scale delta carried by the CSS transform. */
  get factor(): number {
    return this.scale / this.rasterScale;
  }

  /** Everything a scrollbar needs: translation, on-screen content size, viewport
   *  size. Content size is `intrinsic × factor` (i.e. its current pixel size). */
  get metrics(): {
    tx: number;
    ty: number;
    contentW: number;
    contentH: number;
    viewW: number;
    viewH: number;
  } {
    return {
      // The content box's on-screen START — its top-left, which is offset from the
      // world origin by the content origin (`ox,oy`). The scrollbars read this as
      // the scrolled amount, so a negative-origin infinite canvas scrolls correctly.
      tx: this.tx + this.factor * this.ox,
      ty: this.ty + this.factor * this.oy,
      contentW: this.contentW * this.factor,
      contentH: this.contentH * this.factor,
      viewW: this.viewW,
      viewH: this.viewH,
    };
  }

  /** Current viewport size in screen px (drives the visible-scene-rect the infinite
   *  canvas unions into its roam bounds). */
  get viewportSize(): { w: number; h: number } {
    return { w: this.viewW, h: this.viewH };
  }

  attach(world: HTMLElement | null): void {
    this.world = world;
    this.render();
  }

  setLimits(min: number, max: number): void {
    this.min = min;
    this.max = max;
  }

  /** A gesture is live, so the clamp is elastic rather than a wall. */
  private elastic = false;
  private springRaf = 0;
  /**
   * The UNDAMPED translate while a gesture is live — where the finger actually
   * is, before the rubber band is applied.
   *
   * Without it the damping compounded. `panBy` is `t += d; clampTranslate()`,
   * so the elastic branch re-damped a value it had already damped, once per
   * pointer-move: the overshoot was multiplied by RUBBER every frame. Measured
   * before the fix — pan 193.8px out of bounds and then hold PERFECTLY STILL,
   * and the page drifted to 70.5px over five frames. That is the "glitchy,
   * jumpy" panning, and it was worst on touch because that is where elastic
   * panning is the normal way to move.
   *
   * `tx`/`ty` stay the DISPLAYED value, because `screenToWorld` and the
   * editors' `toScene` read them — deriving ink coordinates from an undamped
   * translate would land strokes in the wrong place while overscrolled.
   */
  private rawTx = 0;
  private rawTy = 0;
  /** Set only around a pan's own clamp, so `clampTranslate` knows `tx`/`ty`
   *  hold the previous DAMPED value and `raw` is the live source of truth. */
  private panningRaw = false;
  /**
   * Space kept clear at the top of the viewport, in screen px.
   *
   * The markup toolbar floats over the surface and takes pointer events, so
   * paper underneath it can be neither inked nor erased. CanvasView seats the
   * first page below it — but `seat()` ends in `clampTranslate()`, whose
   * ceiling was `margin` (64), so a 105px seat was yanked back to 64 on the
   * first frame and the page opened under the bar anyway. The clamp has to
   * know about the reserve too, or it will keep undoing the seat.
   */
  private topReserve = 0;

  /** Viewport size in screen px (drives centring + clamp bounds). */
  setViewportSize(w: number, h: number): void {
    this.viewW = w;
    this.viewH = h;
  }

  /** Update the viewport size in response to a CONTAINER resize (a bar toggling,
   *  the dock reflowing), keeping the world point at the visual CENTRE fixed.
   *
   *  The transform is anchored to the container's top-left, so a naive resize
   *  drifts the viewed point: when the container shrinks by Δ, its centre moves
   *  by Δ/2, and the content (translated by an unchanged `tx`) no longer sits
   *  under it. Holding the centre point means translating by exactly that Δ/2 —
   *  factor-independent, since the anchored world point and `factor` are both
   *  unchanged across the resize. This ONLY fires on a size change (never on a
   *  transform change), so it can't fight a deliberate user pan/zoom.
   *
   *  `anchorCentre` false just re-clamps to the new box (used when the surface
   *  deliberately re-fits to the new width instead of preserving the centre). */
  resizeViewport(w: number, h: number, anchorCentre = true): void {
    const oldW = this.viewW;
    const oldH = this.viewH;
    this.viewW = w;
    this.viewH = h;
    if (anchorCentre && oldW > 0 && oldH > 0) {
      this.tx += (w - oldW) / 2;
      this.ty += (h - oldH) / 2;
    }
    this.clampTranslate();
    this.render();
  }

  /** Intrinsic content size in rasterScale-space (the world layout box). Origin is
   *  implicitly (0,0) — the paged world starts at its top-left. */
  setContentSize(w: number, h: number): void {
    this.contentW = w;
    this.contentH = h;
  }

  /** Content bounds WITH an origin (top-left in intrinsic/scene space) — the
   *  infinite canvas's roam box, whose ink can begin at negative scene coords.
   *  Feeds the same clamp + scrollbar math as {@link setContentSize}, offset by the
   *  origin so the box is placed correctly in the world. */
  setContentBounds(ox: number, oy: number, w: number, h: number): void {
    this.ox = ox;
    this.oy = oy;
    this.contentW = w;
    this.contentH = h;
  }

  /** Write the matrix to the world element and notify. transform-origin is the
   *  world's own top-left (0 0); `translate3d` keeps it on the compositor. */
  render(): void {
    if (this.world) {
      this.world.style.transformOrigin = "0 0";
      this.world.style.transform = `translate3d(${this.tx}px, ${this.ty}px, 0) scale(${this.factor})`;
    }
    this.onChange?.();
  }

  panBy(dx: number, dy: number): void {
    if (this.elastic) {
      // Accumulate where the finger is; the clamp derives what is drawn.
      this.rawTx += dx;
      this.rawTy += dy;
      this.panningRaw = true;
      this.clampTranslate();
      this.panningRaw = false;
    } else {
      this.tx += dx;
      this.ty += dy;
      this.clampTranslate();
    }
    this.render();
  }

  /** Change the reserved strip at the top (the markup toolbar comes and goes
   *  with the mode) and re-settle the camera against it. */
  setTopReserve(px: number): void {
    const next = Math.max(0, px);
    if (next === this.topReserve) return;
    this.topReserve = next;
    this.clampTranslate();
    this.render();
  }

  /** Multiply the scale by `factor`, holding the screen point (px,py) fixed. */
  zoomAboutPoint(px: number, py: number, factor: number): void {
    const sNext = clamp(this.scale * factor, this.min, this.max);
    if (sNext === this.scale) return;
    this.tx = anchorTranslate(this.tx, px, this.scale, sNext);
    this.ty = anchorTranslate(this.ty, py, this.scale, sNext);
    this.scale = sNext;
    this.clampTranslate();
    this.render();
    this.scheduleCommit();
  }

  /** Set an absolute scale, holding (px,py) fixed (default: viewport centre).
   *  Used by the zoom buttons and fit-width. */
  zoomTo(scale: number, px?: number, py?: number): void {
    const ax = px ?? this.viewW / 2;
    const ay = py ?? this.viewH / 2;
    const sNext = clamp(scale, this.min, this.max);
    if (sNext === this.scale) {
      this.clampTranslate();
      this.render();
      return;
    }
    this.tx = anchorTranslate(this.tx, ax, this.scale, sNext);
    this.ty = anchorTranslate(this.ty, ay, this.scale, sNext);
    this.scale = sNext;
    this.clampTranslate();
    this.render();
    this.scheduleCommit();
  }

  /** Jump to an exact transform (used to seed fit-width on open). */
  setTransform(tx: number, ty: number, scale: number): void {
    this.scale = clamp(scale, this.min, this.max);
    this.tx = tx;
    this.ty = ty;
    this.clampTranslate();
    this.render();
  }

  /** Seat the view at an exact transform with the content freshly rasterized at
   *  `scale` (so factor == 1, no soft delta). Used for fit-width / first layout. */
  seat(tx: number, ty: number, scale: number): void {
    const s = clamp(scale, this.min, this.max);
    this.rasterScale = s;
    this.scale = s;
    this.tx = tx;
    this.ty = ty;
    this.clampTranslate();
    this.render();
  }

  screenToWorld(px: number, py: number): { x: number; y: number } {
    return { x: (px - this.tx) / this.factor, y: (py - this.ty) / this.factor };
  }

  worldToScreen(x: number, y: number): { x: number; y: number } {
    return { x: this.tx + this.factor * x, y: this.ty + this.factor * y };
  }

  // ---- raster commit -------------------------------------------------------

  /** Request a re-raster after a short stillness; each call resets the timer. */
  scheduleCommit(): void {
    this.cancelCommit();
    this.commitTimer = setTimeout(() => this.flushCommit(), this.commitDelay);
  }

  cancelCommit(): void {
    if (this.commitTimer !== null) clearTimeout(this.commitTimer);
    this.commitTimer = null;
  }

  /** Fire the re-raster request now (no-op when already sharp). Deferred — not
   *  dropped — while `canCommit` is false, so a commit pending behind an active
   *  pen stroke still lands once the stroke ends. */
  flushCommit(): void {
    this.cancelCommit();
    if (Math.abs(this.factor - 1) < 1e-3) return; // already sharp
    if (!this.canCommit()) {
      this.scheduleCommit();
      return;
    }
    this.onCommit?.(this.scale);
  }

  /** Called by the viewer once pages have been re-rasterized at the captured
   *  scale: adopt it as the new rasterScale. tx/ty are unchanged because the
   *  anchoring math is rasterScale-free, so there is NO visual jump (f -> 1). */
  commitRaster(rasterScale: number): void {
    this.rasterScale = rasterScale;
    this.clampTranslate(); // content box changed size; keep it on-screen
    this.render();
  }

  // ---- clamping ------------------------------------------------------------

  /** Keep the document from drifting fully off-screen: allow up to `margin` px
   *  of slack past each edge; centre an axis whose content is smaller than the
   *  viewport. No-op until viewport + content sizes are known. */
  /**
   * Let a drag pull PAST the clamp, with resistance, and spring back on release.
   *
   * Panning off the left of the page used to hit a wall at 64px of slack — the
   * page simply stopped, with no sign of which way it had gone. A notebook does
   * not behave that way: the paper resists, then snaps home. Two pieces make
   * that work: `elastic` (the gesture is live, so overshoot is allowed and
   * damped) and `springHome` (the finger lifted, so run back to the clamp).
   */
  setElastic(on: boolean): void {
    if (this.elastic === on) return;
    this.elastic = on;
    if (on) {
      // The rubber band starts from wherever the camera is now.
      this.rawTx = this.tx;
      this.rawTy = this.ty;
      return;
    }
    // `tx`/`ty` already hold the damped value that is on screen, so the spring
    // starts from what the eye last saw — no jump at the moment of release.
    this.springHome();
  }

  /** Ease the translate back inside the clamp. Cancelled by the next gesture,
   *  and never started while a pen stroke is live — the paper must not move
   *  under the nib. */
  private springHome(): void {
    if (this.springRaf) cancelAnimationFrame(this.springRaf);
    const step = () => {
      this.springRaf = 0;
      if (this.elastic) return; // a new gesture took over
      const beforeX = this.tx;
      const beforeY = this.ty;
      this.clampTranslate();
      const targetX = this.tx;
      const targetY = this.ty;
      this.tx = beforeX;
      this.ty = beforeY;
      const dx = targetX - this.tx;
      const dy = targetY - this.ty;
      if (Math.abs(dx) < 0.4 && Math.abs(dy) < 0.4) {
        this.tx = targetX;
        this.ty = targetY;
        this.render();
        return;
      }
      // Further out, harder home. One rate for both axes, taken from the
      // larger remaining distance, so a diagonal release returns along the
      // line it was dragged rather than bending as one axis outruns the other.
      const far = Math.max(Math.abs(dx), Math.abs(dy));
      const k = Math.min(SPRING_MAX, SPRING_MIN + (far / SPRING_RAMP) * (SPRING_MAX - SPRING_MIN));
      this.tx += dx * k;
      this.ty += dy * k;
      this.render();
      this.springRaf = requestAnimationFrame(step);
    };
    this.springRaf = requestAnimationFrame(step);
  }

  private clampTranslate(): void {
    // While a gesture is live the clamp is a SUGGESTION: overshoot is allowed
    // and damped, so the page keeps moving under the finger but fights back
    // harder the further it goes. On release springHome runs the rest.
    if (this.elastic) {
      const softened = (raw: number, clamped: number) => {
        const over = raw - clamped;
        if (over === 0) return raw;
        // Linear, so it never runs out of travel. The page lags the finger by a
        // constant fraction — enough to feel like tension, never enough to feel
        // like a wall.
        return clamped + over * RUBBER;
      };
      // Anything that moved the camera OTHER than a pan (a pinch, a resize)
      // re-bases the band: that new position is where the finger effectively
      // is now. Only a pan is allowed to accumulate against the old baseline.
      if (!this.panningRaw) {
        this.rawTx = this.tx;
        this.rawTy = this.ty;
      }
      if (this.viewW > 0 && this.contentW > 0) {
        const start = this.rawTx + this.factor * this.ox;
        const hard = clamp1D(start, this.contentW * this.factor, this.viewW, this.margin);
        this.tx = softened(start, hard) - this.factor * this.ox;
      }
      if (this.viewH > 0 && this.contentH > 0) {
        const start = this.rawTy + this.factor * this.oy;
        const hard = clamp1D(
          start,
          this.contentH * this.factor,
          this.viewH,
          this.margin,
          this.topReserve,
        );
        this.ty = softened(start, hard) - this.factor * this.oy;
      }
      return;
    }
    return this.hardClamp();
  }

  private hardClamp(): void {
    // Clamp the content box's on-screen START (`t + factor·origin`), then back the
    // raw translate out of it. With origin 0 (the paged world) this is exactly the
    // previous `clamp1D(t, …)`; with a negative origin it bounds the real box.
    if (this.viewW > 0 && this.contentW > 0) {
      const start = this.tx + this.factor * this.ox;
      this.tx = clamp1D(start, this.contentW * this.factor, this.viewW, this.margin) - this.factor * this.ox;
    }
    if (this.viewH > 0 && this.contentH > 0) {
      const start = this.ty + this.factor * this.oy;
      this.ty =
        clamp1D(start, this.contentH * this.factor, this.viewH, this.margin, this.topReserve) -
        this.factor * this.oy;
    }
  }
}

/** Clamp one translate axis. If the (scaled) content is smaller than the
 *  viewport, centre it; otherwise bound translation so at most `margin` px of
 *  empty space shows past either edge. Exported for tests. */
export const clamp1D = (
  t: number,
  contentLen: number,
  viewLen: number,
  margin: number,
  /**
   * Screen px kept clear at the START of this axis — the strip the markup
   * toolbar floats over. Zero on the horizontal axis and for every surface
   * that has no reserve.
   *
   * It has to be honoured in BOTH branches. The centring branch ignored the
   * requested offset entirely, which is the fresh-note case on a tablet (one
   * page, shorter than the viewport): the page was centred in the whole
   * viewport and its top landed under the bar however the surface was seated.
   */
  reserve = 0,
): number => {
  if (contentLen + reserve <= viewLen) {
    // Centre in the space BELOW the reserve, not in the whole viewport.
    return reserve + (viewLen - reserve - contentLen) / 2;
  }
  const min = viewLen - contentLen - margin; // content bottom/right edge limit
  const max = margin + reserve; // content top/left edge limit
  return clamp(t, min, max);
};
